/* @sravni/react-design-system version 0.2.71 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, isDefined } from '@sravni/react-utils';
import { ArrowLeft, ArrowRight } from '@sravni/react-icons';
import '../../Icon/index.js';
import '../../Space/index.js';
import modules_2fab7c71 from '../styles/Carousel.module.less.js';
import { Space } from '../../Space/src/Space.js';
import { Icon } from '../../Icon/src/Icon.js';

/**
 * @description A carousel component.
 *
 * @component
 * @example
 * ```jsx
 * <Carousel>
 *   <div>1</div>
 *   <div>2</div>
 * </Carousel>
 * ```
 */
var Carousel = React.forwardRef(function (props, ref) {
    var title = props.title, gradient = props.gradient, bottomControls = props.bottomControls, className = props.className, slidesClassName = props.slidesClassName, children = props.children, restProps = __rest(props, ["title", "gradient", "bottomControls", "className", "slidesClassName", "children"]);
    var cx = useStyles(modules_2fab7c71);
    var _a = __read(React.useState(0), 2), delta = _a[0], setDelta = _a[1];
    var _b = __read(React.useState(false), 2), isReachedRight = _b[0], setIsReachedRight = _b[1];
    var _c = __read(React.useState(false), 2), isReachedLeft = _c[0], setIsReachedLeft = _c[1];
    var _d = __read(React.useState(0), 2), margin = _d[0], setMargin = _d[1];
    var _e = __read(React.useState(0), 2), scrollPosition = _e[0], setScrollPosition = _e[1];
    var refWrapper = React.useRef(null);
    var startOffsetWidth = React.useRef();
    var isArrowsHidden = React.useMemo(function () { return scrollPosition === 0 && !isReachedRight; }, [isReachedRight]);
    var isHeaderVisible = title || (!isArrowsHidden && !bottomControls);
    var headerClassNames = cx('header', {
        hidden: !title,
    });
    var slidesClassNames = cx('slides', {
        gradient: gradient,
        leftGradient: isReachedLeft,
        rightGradient: isReachedRight,
    });
    var divStyle = React.useMemo(function () {
        return ({
            '--position': "".concat(margin, "px"),
        });
    }, [margin]);
    var handleResize = React.useCallback(function (forceUpdate) {
        var hasOverflow = !!(refWrapper === null || refWrapper === void 0 ? void 0 : refWrapper.current) && refWrapper.current.offsetWidth < refWrapper.current.scrollWidth;
        if (!!(refWrapper === null || refWrapper === void 0 ? void 0 : refWrapper.current) && hasOverflow && (delta === 0 || forceUpdate)) {
            var offsetWidth = forceUpdate && (startOffsetWidth === null || startOffsetWidth === void 0 ? void 0 : startOffsetWidth.current) ? startOffsetWidth.current : refWrapper.current.offsetWidth;
            setDelta(offsetWidth - refWrapper.current.scrollWidth || 0);
        }
        setIsReachedRight(hasOverflow);
    }, [delta]);
    var scroll = React.useCallback(function (position) {
        var _a;
        var current = refWrapper === null || refWrapper === void 0 ? void 0 : refWrapper.current;
        var hiddenElementsWidth = !!current &&
            Array.from(current === null || current === void 0 ? void 0 : current.children)
                .slice(0, position)
                .reduce(function (acc, el) { return acc + el.offsetWidth; }, 0);
        var openElementsWidth = current
            ? Array.from(current === null || current === void 0 ? void 0 : current.children)
                .slice(position)
                .reduce(function (acc, el) {
                var width = el instanceof HTMLElement ? el.offsetWidth : 0;
                return acc + width;
            }, 0)
            : 0;
        if (position > 0 &&
            ((_a = refWrapper === null || refWrapper === void 0 ? void 0 : refWrapper.current) === null || _a === void 0 ? void 0 : _a.parentElement) &&
            openElementsWidth < refWrapper.current.parentElement.offsetWidth) {
            setMargin(delta);
            setScrollPosition(position);
        }
        else {
            setMargin(-hiddenElementsWidth);
            setScrollPosition(position);
        }
        setTimeout(function () { return handleResize(); }, 300);
        setIsReachedLeft(!!position);
    }, [handleResize, delta, refWrapper]);
    var handleOnClickRightArrow = React.useCallback(function () {
        scroll(scrollPosition + 1);
    }, [scroll, scrollPosition]);
    var handleOnClickLeftArrow = React.useCallback(function () {
        scroll(scrollPosition - 1);
    }, [scroll, scrollPosition]);
    var handleWheelScroll = React.useCallback(function (e) {
        var isReachedRight = e.target.scrollWidth - e.target.scrollLeft === e.target.clientWidth;
        setIsReachedRight(!isReachedRight);
        setIsReachedLeft(e.target.scrollLeft !== 0);
    }, []);
    var slides = React.useMemo(function () {
        return React.Children.map(children, function (child) {
            var _a, _b;
            return (React.createElement("div", { style: { width: (_a = child.props.style) === null || _a === void 0 ? void 0 : _a.width, height: (_b = child.props.style) === null || _b === void 0 ? void 0 : _b.height } }, child));
        });
    }, [children]);
    var arrows = React.useMemo(function () { return (React.createElement(Space, { align: "end", size: 12, className: cx('arrows', 'important') },
        React.createElement(Icon, { className: cx('arrow', 'leftArrow', { disabled: scrollPosition === 0 }), size: 16, shape: "round", onClick: handleOnClickLeftArrow },
            React.createElement(ArrowLeft, null)),
        React.createElement(Icon, { className: cx('arrow', 'rightArrow', { disabled: !isReachedRight }), size: 16, shape: "round", onClick: handleOnClickRightArrow },
            React.createElement(ArrowRight, null)))); }, [scrollPosition, isReachedRight, handleOnClickLeftArrow, handleOnClickRightArrow]);
    React.useEffect(function () {
        var cb = function () {
            var _a, _b;
            var hasOverflow = !!(refWrapper === null || refWrapper === void 0 ? void 0 : refWrapper.current) && refWrapper.current.offsetWidth < refWrapper.current.scrollWidth;
            if (!!(refWrapper === null || refWrapper === void 0 ? void 0 : refWrapper.current) && hasOverflow) {
                setDelta(margin + ((_a = refWrapper.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) - ((_b = refWrapper.current) === null || _b === void 0 ? void 0 : _b.scrollWidth) || 0);
            }
            setIsReachedRight(hasOverflow);
        };
        window.addEventListener('resize', cb);
        return function () { return window.removeEventListener('resize', cb); };
    }, [refWrapper, delta, scrollPosition]);
    React.useEffect(function () {
        handleResize(true);
    }, [children]);
    React.useEffect(function () {
        var _a;
        if (!isDefined(startOffsetWidth)) {
            return;
        }
        startOffsetWidth.current = (_a = refWrapper.current) === null || _a === void 0 ? void 0 : _a.offsetWidth;
    }, []);
    return (React.createElement("div", __assign({ ref: ref, "data-qa": "Carousel", className: cx(className, 'container'), style: divStyle }, restProps),
        isHeaderVisible && (React.createElement(Space, { justify: "space-between", className: headerClassNames },
            React.createElement("div", { className: cx('title') }, title),
            !isArrowsHidden && !bottomControls && arrows)),
        React.createElement("div", { className: slidesClassNames },
            React.createElement("div", { ref: refWrapper, "data-qa": "Slides", className: cx(slidesClassName, 'wrapper'), onScroll: handleWheelScroll }, slides)),
        !isArrowsHidden && bottomControls && (React.createElement(Space, { justify: "end", className: cx('bottomControls') }, arrows))));
});
Carousel.defaultProps = {
    bottomControls: false,
    gradient: false,
};
Carousel.displayName = 'Carousel';

export { Carousel };
//# sourceMappingURL=Carousel.js.map
